import { tokenizeMentions } from "@/lib/mentionParser";
import type { MemberProfile } from "@/lib/models";

interface MentionTextProps {
  text: string;
  members: MemberProfile[];
  className?: string;
}

/**
 * Renders message text with `@nickname` / `@name` tokens highlighted.
 * Mirrors iOS `MentionRenderer.attributedText(_:memberMap:)`
 * (referenced from `AddEditPostView.swift:241`) and the Android
 * `MentionAnnotatedString` styled-spans renderer. Resolved tokens are
 * rendered in accent color + bold; unresolved tokens (e.g.
 * `@stranger`) are rendered as plain text.
 *
 * Web previously displayed mentions as raw `@token` text, which made
 * posts and chats look broken whenever someone @-mentioned a household
 * member. Tokenization is in `lib/mentionParser.tokenizeMentions` so
 * the parsing logic is unit-testable without a DOM.
 */
export function MentionText({ text, members, className }: MentionTextProps) {
  const parts = tokenizeMentions(text, members);
  return (
    <span className={className}>
      {parts.map((part, i) => {
        if (part.kind === "text") return <span key={i}>{part.value}</span>;
        if (part.uid) {
          return (
            <span
              key={i}
              className="font-semibold text-[color:var(--accent-color,#00C7BE)]"
            >
              {part.token}
            </span>
          );
        }
        return <span key={i}>{part.token}</span>;
      })}
    </span>
  );
}
